import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [showConfidentialityModal, setShowConfidentialityModal] = useState(false);
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [confidentialitySubmitting, setConfidentialitySubmitting] = useState(false);
  const [confidentialitySuccess, setConfidentialitySuccess] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setNewsletterSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4kgm1usn5v8oqu6pdk0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setNewsletterSuccess(true);
        form.reset();
        setTimeout(() => {
          setShowNewsletterModal(false);
          setNewsletterSuccess(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  const handleConfidentialitySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const form = e.currentTarget;
    const formData = new FormData(form);
    const signature = formData.get('signature') as string;
    
    if (!signature || signature.trim().length < 2) {
      alert('Please provide your signature');
      return;
    }

    setConfidentialitySubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/d4kgm1usn5v8oqu6pdkg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setConfidentialitySuccess(true);
        form.reset();
        setTimeout(() => {
          setShowConfidentialityModal(false);
          setConfidentialitySuccess(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setConfidentialitySubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center">
                <i className="ri-cup-line text-white text-xl"></i>
              </div>
              <span className={`text-xl font-semibold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                Tea and Talk with Risi PMHNP
              </span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <a href="#community" className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}>Community</a>
              <a href="#services" className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}>Services</a>
              <a href="#resources" className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}>Resources</a>
              <a href="#about" className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}>About</a>
              <a href="tel:3037256096" className="px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-colors whitespace-nowrap">
                Get Free Support
              </a>
            </div>

            <button 
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden w-8 h-8 flex items-center justify-center"
            >
              <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>

          {showMobileMenu && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              <a href="#community" className="block text-sm font-medium text-gray-700 hover:text-teal-600">Community</a>
              <a href="#services" className="block text-sm font-medium text-gray-700 hover:text-teal-600">Services</a>
              <a href="#resources" className="block text-sm font-medium text-gray-700 hover:text-teal-600">Resources</a>
              <a href="#about" className="block text-sm font-medium text-gray-700 hover:text-teal-600">About</a>
              <a href="tel:3037256096" className="block px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-full text-center">
                Get Free Support
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/97be066482875b82b2fddabc195c0943/407d3d1a935cebe3a69749370e152385.jpeg"
            alt="Tea and Talk with Risi PMHNP"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 text-center">
          <div className="inline-block px-4 py-2 border border-white/30 rounded-full mb-6 backdrop-blur-sm">
            <span className="text-white text-sm font-medium">Free Support for Women's Mental Wellness</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#community" className="px-8 py-4 bg-teal-600 text-white text-base font-semibold rounded-full hover:bg-teal-700 transition-all hover:scale-105 whitespace-nowrap">
              Join Our Community
            </a>
            <a href="tel:3037256096" className="px-8 py-4 bg-white/10 backdrop-blur-md text-white text-base font-semibold rounded-full border-2 border-white/30 hover:bg-white/20 transition-all whitespace-nowrap">
              Call for Free Support
            </a>
          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-8 text-white">
            <div className="flex items-center gap-2">
              <i className="ri-phone-line text-xl"></i>
              <span className="text-sm">3037256096</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-printer-line text-xl"></i>
              <span className="text-sm">Fax: 7209640401</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-mail-line text-xl"></i>
              <a href="mailto:teatalkwithrisipmhnp@gmail.com" className="text-sm hover:text-teal-200 transition-colors">
                teatalkwithrisipmhnp@gmail.com
              </a>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-global-line text-xl"></i>
              <a href="https://teatalkwithrisipmhnp.com" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-teal-200 transition-colors">
                teatalkwithrisipmhnp.com
              </a>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-instagram-line text-xl"></i>
              <a href="https://www.instagram.com/tea_and_talk_with_risi_pmhnp" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-teal-200 transition-colors">
                @tea_and_talk_with_risi_pmhnp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="w-full h-[500px] rounded-3xl overflow-hidden shadow-xl">
                <img 
                  src="https://static.readdy.ai/image/97be066482875b82b2fddabc195c0943/3a80f02c49b6f746989435307bde3b20.jpeg"
                  alt="Risi PMHNP Professional"
                  className="w-full h-full object-cover object-[center_20%]"
                />
              </div>
              <div className="absolute top-6 left-6 flex flex-col gap-3">
                <div className="px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg">
                  <span className="text-sm font-semibold text-teal-700">PMHNP Certified</span>
                </div>
                <div className="px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg">
                  <span className="text-sm font-semibold text-teal-700">Trauma-Informed Care</span>
                </div>
                <div className="px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg">
                  <span className="text-sm font-semibold text-teal-700">Peer Support Facilitator</span>
                </div>
              </div>
            </div>

            <div>
              <div className="inline-block px-4 py-1.5 bg-teal-100 text-teal-700 text-sm font-medium rounded-full mb-4">
                Your Guide to Wellness
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Meet Risi, PMHNP
              </h2>
              
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="px-4 py-2 border-2 border-teal-600 text-teal-700 text-sm font-medium rounded-full">Peer Support</span>
                <span className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-full">Women's Health</span>
                <span className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-full">Community Facilitator</span>
              </div>

              <div className="bg-gray-800 rounded-2xl p-8 text-white">
                <div className="mb-4">
                  <span className="px-3 py-1 bg-teal-600 text-white text-xs font-semibold rounded-full">Licensed Professional</span>
                </div>
                <p className="text-base leading-relaxed mb-4">
                  As a board-certified Psychiatric Mental Health Nurse Practitioner, I facilitate compassionate peer support groups where women can connect, share, and heal together. My approach combines professional guidance with the power of community support.
                </p>
                <p className="text-base leading-relaxed">
                  Through Solution Healthcare, I've created a safe space where women support each other through life's challenges. Our peer groups provide the understanding, encouragement, and connection that comes from shared experiences.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Benefits */}
      <section id="community" className="py-24 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <p className="text-sm text-gray-600 mb-3">Why Join Our Community</p>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
              A Space Designed<br />
              For Women, By Women
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-rose-100 rounded-2xl p-8 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-rose-200 rounded-full flex items-center justify-center mb-6">
                <i className="ri-team-line text-3xl text-rose-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Peer Support Network</h3>
              <p className="text-gray-700 leading-relaxed">
                Connect with women who understand your journey. Share experiences, offer encouragement, and build lasting friendships in a judgment-free environment where vulnerability is strength.
              </p>
            </div>

            <div className="bg-teal-100 rounded-2xl p-8 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-teal-200 rounded-full flex items-center justify-center mb-6">
                <i className="ri-calendar-heart-line text-3xl text-teal-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Local Meetups & Events</h3>
              <p className="text-gray-700 leading-relaxed">
                Participate in organized gatherings, wellness workshops, and support groups in your area. Build real-world connections beyond the digital space through meaningful activities.
              </p>
            </div>

            <div className="bg-amber-100 rounded-2xl p-8 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-amber-200 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-amber-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Vetted Resources</h3>
              <p className="text-gray-700 leading-relaxed">
                Access curated mental health resources, crisis support information, and professional referrals carefully reviewed by licensed practitioners to ensure quality care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <p className="text-sm text-teal-600 font-medium mb-3">Free Peer Support</p>
            <h2 className="text-5xl font-bold text-gray-900 mb-2">
              What We Offer
            </h2>
            <p className="text-3xl font-serif italic text-gray-600">Community-Based Support</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="group rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all cursor-pointer">
              <div className="h-80 overflow-hidden">
                <img 
                  src="https://public.readdy.ai/ai/img_res/c189605d7e429328a0d676879e2362af.jpg"
                  alt="Weekly Support Groups"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="bg-white p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Weekly Support Groups</h3>
                <p className="text-gray-600 text-sm">Regular peer-led sessions where women share experiences and support each other</p>
              </div>
            </div>

            <div className="group rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all cursor-pointer">
              <div className="h-80 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=One%20on%20one%20peer%20support%20conversation%20between%20two%20women%2C%20comfortable%20private%20setting%20with%20tea%20and%20tissues%2C%20compassionate%20listening%20and%20emotional%20support%2C%20safe%20space%20for%20sharing%20personal%20struggles%2C%20warm%20natural%20lighting&width=500&height=600&seq=service2&orientation=portrait"
                  alt="One-on-One Peer Support"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="bg-white p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">One-on-One Peer Support</h3>
                <p className="text-gray-600 text-sm">Connect with a peer supporter for personalized guidance and encouragement</p>
              </div>
            </div>

            <div className="group rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all cursor-pointer">
              <div className="h-80 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Crisis%20support%20hotline%20and%20resources%20display%2C%20emergency%20contact%20information%20and%20mental%20health%20resources%2C%20supportive%20materials%20and%20helpline%20numbers%2C%20accessible%20crisis%20intervention%20tools%2C%20calming%20and%20organized%20presentation&width=500&height=600&seq=service3&orientation=portrait"
                  alt="Crisis Support Resources"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="bg-white p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Crisis Support Resources</h3>
                <p className="text-gray-600 text-sm">Access to vetted crisis hotlines and immediate support when you need it most</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 bg-teal-600 rounded-full"></div>
              <span className="text-sm text-teal-600 font-medium">Member Stories</span>
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">
              Hear From Our<br />Thriving Community
            </h2>
            <p className="text-gray-600 text-lg">Real experiences from women who found support and healing</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                name: "Sarah M.",
                role: "Community Member",
                text: "Finally found my tribe! This community has been a lifeline during my toughest moments. The support and understanding I've received here is unmatched.",
                rating: 5
              },
              {
                name: "Jennifer K.",
                role: "Patient",
                text: "Risi's professional care truly listens. She takes time to understand my unique situation and creates treatment plans that actually work for my life.",
                rating: 5
              },
              {
                name: "Maria R.",
                role: "Community Member",
                text: "Safe space to be vulnerable and authentic. The women here lift each other up and celebrate every victory, no matter how small.",
                rating: 5
              },
              {
                name: "Amanda L.",
                role: "Patient",
                text: "Compassionate and knowledgeable care that addresses both my mental health needs and respects my journey. Risi is truly exceptional at what she does.",
                rating: 5
              },
              {
                name: "Rachel T.",
                role: "Community Member",
                text: "Life-changing support system. I never thought I'd find a place where I could be so open about my struggles without judgment. This community changed everything.",
                rating: 5
              },
              {
                name: "Lisa W.",
                role: "Patient",
                text: "Holistic approach that works for my whole self, not just symptoms. The combination of professional care and community support is powerful.",
                rating: 5
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-lg"></i>
                  ))}
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{testimonial.text.split('.')[0]}</h4>
                <p className="text-gray-700 leading-relaxed mb-6">{testimonial.text}</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    <span className="text-teal-700 font-bold text-lg">{testimonial.name[0]}</span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{testimonial.name}</p>
                    <p className="text-gray-600 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-3xl p-12 md:p-16 text-center shadow-2xl">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-mail-line text-4xl text-white"></i>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Stay Connected
            </h2>
            <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
              Join our newsletter for wellness tips, community updates, and mental health resources delivered to your inbox
            </p>
            <button 
              onClick={() => setShowNewsletterModal(true)}
              className="px-10 py-4 bg-white text-teal-700 text-base font-semibold rounded-full hover:bg-teal-50 transition-all hover:scale-105 whitespace-nowrap"
            >
              Subscribe to Newsletter
            </button>
          </div>
        </div>
      </section>

      {/* Support Our Mission Section - NEW */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-1.5 bg-teal-100 text-teal-700 text-sm font-medium rounded-full mb-4">
              Support Our Mission
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">
              Help Us Grow Our Community
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We accept volunteers and financial support from the community to continue providing free mental health support to women in need
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-3xl p-10 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-hand-heart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Volunteer With Us</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Share your time and talents to support our community. Whether you're a peer supporter, event organizer, or simply want to help, we welcome your contribution.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-teal-600 text-xl"></i>
                  <span className="text-sm">Peer Support Facilitators</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-teal-600 text-xl"></i>
                  <span className="text-sm">Event Coordinators</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-teal-600 text-xl"></i>
                  <span className="text-sm">Community Outreach</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-teal-600 text-xl"></i>
                  <span className="text-sm">Administrative Support</span>
                </li>
              </ul>
              <a 
                href="tel:3037256096" 
                className="inline-block w-full px-8 py-4 bg-teal-600 text-white text-base font-semibold rounded-full hover:bg-teal-700 transition-all hover:scale-105 text-center whitespace-nowrap"
              >
                Become a Volunteer
              </a>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-10 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-gift-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Financial Support</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Your financial contribution helps us keep our services free and accessible to all women who need support. Every donation makes a difference.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-amber-600 text-xl"></i>
                  <span className="text-sm">Fund Free Support Groups</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-amber-600 text-xl"></i>
                  <span className="text-sm">Provide Resources & Materials</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-amber-600 text-xl"></i>
                  <span className="text-sm">Expand Community Programs</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-amber-600 text-xl"></i>
                  <span className="text-sm">Support Crisis Services</span>
                </li>
              </ul>
              <a 
                href="tel:3037256096" 
                className="inline-block w-full px-8 py-4 bg-amber-600 text-white text-base font-semibold rounded-full hover:bg-amber-700 transition-all hover:scale-105 text-center whitespace-nowrap"
              >
                Make a Donation
              </a>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-block bg-teal-50 border-2 border-teal-600 rounded-2xl p-6 max-w-3xl">
              <p className="text-teal-900 font-semibold text-base">
                💚 Your support helps us maintain a safe, welcoming space where women can heal and grow together. Thank you for believing in our mission.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-amber-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="rounded-3xl overflow-hidden shadow-2xl mb-12">
            <div className="h-96 relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Diverse%20group%20of%20women%20supporting%20each%20other%20in%20circle%2C%20warm%20and%20inclusive%20community%20gathering%20with%20natural%20lighting%2C%20women%20of%20different%20backgrounds%20holding%20hands%20and%20smiling%2C%20empowering%20sisterhood%20and%20connection%2C%20uplifting%20atmosphere%20with%20soft%20focus%20background&width=1200&height=600&seq=cta1&orientation=landscape"
                alt="Women Supporting Women"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
            </div>
          </div>

          <div className="text-center">
            <h2 className="text-4xl md:text-5xl leading-tight mb-8">
              <span className="text-gray-700">You don't have to face</span><br />
              <span className="font-serif italic text-gray-500">life's challenges</span><br />
              <span className="font-bold text-gray-900">alone anymore</span>
            </h2>
            <div className="bg-teal-50 border-2 border-teal-600 rounded-2xl p-6 mb-8 max-w-2xl mx-auto">
              <p className="text-teal-900 font-semibold text-lg">
                💚 All services are completely free - no insurance required, no hidden costs
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:3037256096" className="inline-block px-12 py-4 bg-gray-900 text-white text-base font-semibold rounded-full hover:bg-gray-800 transition-all hover:scale-105 whitespace-nowrap">
                Join Our Community
              </a>
              <button 
                onClick={() => setShowConfidentialityModal(true)}
                className="inline-block px-12 py-4 bg-teal-600 text-white text-base font-semibold rounded-full hover:bg-teal-700 transition-all hover:scale-105 whitespace-nowrap"
              >
                Review Confidentiality Agreement
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section id="resources" className="py-24 bg-teal-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Mental Health Resources</h2>
            <p className="text-gray-700 text-lg max-w-2xl mx-auto">
              Access vetted resources, crisis support, and wellness tools to support your mental health journey
            </p>
          </div>

          {/* Crisis Support - Priority Section */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <div className="inline-block px-4 py-2 bg-red-100 text-red-700 text-sm font-semibold rounded-full mb-2">
                🆘 Crisis Support Available 24/7
              </div>
              <h3 className="text-3xl font-bold text-gray-900">If You're in Crisis, Help is Available Now</h3>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-red-200">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-phone-line text-2xl text-red-600"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">National Suicide Prevention Lifeline</h4>
                <a href="tel:988" className="text-3xl font-bold text-red-600 hover:text-red-700">988</a>
                <p className="text-gray-600 mt-2 text-sm">Available 24/7 for crisis support</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-purple-200">
                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-message-3-line text-2xl text-purple-600"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Crisis Text Line</h4>
                <p className="text-3xl font-bold text-purple-600">Text HOME to 741741</p>
                <p className="text-gray-600 mt-2 text-sm">Free, 24/7 crisis support via text</p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-3">
                  <i className="ri-heart-pulse-line text-xl text-teal-600"></i>
                </div>
                <h5 className="font-bold text-gray-900 mb-2 text-sm">SAMHSA National Helpline</h5>
                <a href="tel:18006624357" className="text-lg font-bold text-teal-600 hover:text-teal-700">1-800-662-4357</a>
                <p className="text-gray-600 text-xs mt-1">Mental health & substance abuse</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mb-3">
                  <i className="ri-parent-line text-xl text-pink-600"></i>
                </div>
                <h5 className="font-bold text-gray-900 mb-2 text-sm">Postpartum Support International</h5>
                <a href="tel:18009444773" className="text-lg font-bold text-pink-600 hover:text-pink-700">1-800-944-4773</a>
                <p className="text-gray-600 text-xs mt-1">Maternal mental health support</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-3">
                  <i className="ri-shield-user-line text-xl text-orange-600"></i>
                </div>
                <h5 className="font-bold text-gray-900 mb-2 text-sm">National Domestic Violence Hotline</h5>
                <a href="tel:18007997233" className="text-lg font-bold text-orange-600 hover:text-orange-700">1-800-799-7233</a>
                <p className="text-gray-600 text-xs mt-1">24/7 confidential support</p>
              </div>
            </div>
          </div>

          {/* Resource Categories */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-book-open-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">Mental Health Education</h4>
              <ul className="space-y-2">
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
                  <span>Understanding anxiety & depression</span>
                </li>
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
                  <span>Trauma-informed care basics</span>
                </li>
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
                  <span>Coping strategies guide</span>
                </li>
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
                  <span>Recognizing warning signs</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-heart-3-line text-2xl text-amber-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">Self-Care Tools</h4>
              <ul className="space-y-2">
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-600 mt-0.5"></i>
                  <span>Daily wellness worksheets</span>
                </li>
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-600 mt-0.5"></i>
                  <span>Mindfulness exercises</span>
                </li>
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-600 mt-0.5"></i>
                  <span>Journaling prompts</span>
                </li>
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-600 mt-0.5"></i>
                  <span>Breathing techniques</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-2xl text-rose-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">Local Support Services</h4>
              <ul className="space-y-2">
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-rose-600 mt-0.5"></i>
                  <span>Community mental health centers</span>
                </li>
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-rose-600 mt-0.5"></i>
                  <span>Support group directories</span>
                </li>
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-rose-600 mt-0.5"></i>
                  <span>Sliding scale therapy options</span>
                </li>
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-rose-600 mt-0.5"></i>
                  <span>Free clinic information</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-article-line text-2xl text-emerald-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">Wellness Articles</h4>
              <ul className="space-y-2">
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-emerald-600 mt-0.5"></i>
                  <span>Women's mental health topics</span>
                </li>
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-emerald-600 mt-0.5"></i>
                  <span>Peer support best practices</span>
                </li>
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-emerald-600 mt-0.5"></i>
                  <span>Building resilience</span>
                </li>
                <li className="text-sm text-gray-700 flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-emerald-600 mt-0.5"></i>
                  <span>Healthy boundaries guide</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Additional Resources */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Additional Mental Health Resources</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-global-line text-teal-600"></i>
                  National Organizations
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                    <span>National Alliance on Mental Illness (NAMI)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                    <span>Mental Health America (MHA)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                    <span>Anxiety and Depression Association (ADAA)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                    <span>National Institute of Mental Health (NIMH)</span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-smartphone-line text-teal-600"></i>
                  Mental Health Apps & Tools
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                    <span>Meditation & mindfulness apps</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                    <span>Mood tracking tools</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                    <span>Sleep improvement resources</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                    <span>Stress management guides</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm">
              Need help accessing these resources? <a href="tel:3037256096" className="text-teal-600 font-semibold hover:text-teal-700">Call us at 3037256096</a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-teal-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <i className="ri-cup-line text-teal-700 text-xl"></i>
                </div>
                <span className="text-xl font-bold">Tea and Talk with Risi PMHNP</span>
              </div>
              <p className="text-teal-100 mb-6 leading-relaxed">
                A warm gathering space where women connect over tea, share their journeys, and support each other through peer-led community groups.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-lg"></i>
                  <a href="tel:3037256096" className="hover:text-teal-200 transition-colors">3037256096</a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-printer-line text-lg"></i>
                  <span className="text-teal-100">Fax: 7209640401</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-mail-line text-lg"></i>
                  <a href="mailto:teatalkwithrisipmhnp@gmail.com" className="hover:text-teal-200 transition-colors">teatalkwithrisipmhnp@gmail.com</a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-global-line text-lg"></i>
                  <a href="https://teatalkwithrisipmhnp.com" target="_blank" rel="noopener noreferrer" className="hover:text-teal-200 transition-colors">
                    teatalkwithrisipmhnp.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-instagram-line text-lg"></i>
                  <a href="https://www.instagram.com/tea_and_talk_with_risi_pmhnp" target="_blank" rel="noopener noreferrer" className="hover:text-teal-200 transition-colors">
                    @tea_and_talk_with_risi_pmhnp
                  </a>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Community</h4>
              <ul className="space-y-2">
                <li><a href="#community" className="text-teal-100 hover:text-white transition-colors text-sm">Join Free</a></li>
                <li><a href="#testimonials" className="text-teal-100 hover:text-white transition-colors text-sm">Member Stories</a></li>
                <li><a href="#resources" className="text-teal-100 hover:text-white transition-colors text-sm">Free Resources</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Support</h4>
              <ul className="space-y-2">
                <li><a href="#services" className="text-teal-100 hover:text-white transition-colors text-sm">Peer Groups</a></li>
                <li><a href="#services" className="text-teal-100 hover:text-white transition-colors text-sm">Support Options</a></li>
                <li><a href="#about" className="text-teal-100 hover:text-white transition-colors text-sm">No Cost Barrier</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><a href="#resources" className="text-teal-100 hover:text-white transition-colors text-sm">Crisis Support</a></li>
                <li><a href="#resources" className="text-teal-100 hover:text-white transition-colors text-sm">Wellness Blog</a></li>
                <li><a href="#resources" className="text-teal-100 hover:text-white transition-colors text-sm">FAQ</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-teal-600 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-teal-100 text-sm">© 2025 Tea and Talk with Risi PMHNP. All rights reserved.</p>
              <div className="flex items-center gap-6">
                <a href="https://www.instagram.com/tea_and_talk_with_risi_pmhnp" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border-2 border-white/30 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="https://facebook.com/solutionhealthcarellc" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border-2 border-white/30 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors">
                  <i className="ri-facebook-line text-xl"></i>
                </a>
                <a href="mailto:contact@solutionhealthcare.com" className="w-10 h-10 border-2 border-white/30 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors">
                  <i className="ri-mail-line text-xl"></i>
                </a>
              </div>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-teal-100 hover:text-white text-sm transition-colors">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Newsletter Modal */}
      {showNewsletterModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 relative shadow-2xl">
            <button 
              onClick={() => setShowNewsletterModal(false)}
              className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
            >
              <i className="ri-close-line text-2xl text-gray-600"></i>
            </button>

            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-mail-line text-3xl text-teal-600"></i>
            </div>

            <h3 className="text-3xl font-bold text-gray-900 mb-3 text-center">Subscribe to Our Newsletter</h3>
            <p className="text-gray-600 mb-8 text-center">Get wellness tips and community updates delivered to your inbox</p>

            {newsletterSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-4xl text-green-600"></i>
                </div>
                <p className="text-xl font-semibold text-green-600">Successfully Subscribed!</p>
              </div>
            ) : (
              <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                  <input 
                    type="text" 
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input 
                    type="email" 
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input 
                      type="checkbox" 
                      name="interests"
                      value="Mental Health Tips"
                      className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Mental Health Tips</span>
                  </label>
                </div>

                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input 
                      type="checkbox" 
                      name="interests"
                      value="Community Events"
                      className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Community Events</span>
                  </label>
                </div>

                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input 
                      type="checkbox" 
                      name="interests"
                      value="Wellness Resources"
                      className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Wellness Resources</span>
                  </label>
                </div>

                <button 
                  type="submit"
                  disabled={newsletterSubmitting}
                  className="w-full px-8 py-4 bg-teal-600 text-white text-base font-semibold rounded-full hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {newsletterSubmitting ? 'Subscribing...' : 'Subscribe Now'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Confidentiality Agreement Modal */}
      {showConfidentialityModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-3xl w-full p-8 relative shadow-2xl my-8">
            <button 
              onClick={() => setShowConfidentialityModal(false)}
              className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
            >
              <i className="ri-close-line text-2xl text-gray-600"></i>
            </button>

            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shield-check-line text-3xl text-teal-600"></i>
            </div>

            <h3 className="text-3xl font-bold text-gray-900 mb-3 text-center">Confidentiality Agreement</h3>
            <p className="text-gray-600 mb-8 text-center">Please review and sign our confidentiality agreement</p>

            {confidentialitySuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-4xl text-green-600"></i>
                </div>
                <p className="text-xl font-semibold text-green-600">Agreement Signed Successfully!</p>
              </div>
            ) : (
              <form id="confidentiality-form" data-readdy-form onSubmit={handleConfidentialitySubmit} className="space-y-6">
                <div className="bg-gray-50 rounded-xl p-6 max-h-96 overflow-y-auto border border-gray-200">
                  <h4 className="font-bold text-gray-900 mb-4 text-lg">Community Confidentiality Agreement</h4>
                  
                  <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
                    <p>
                      <strong>Effective Date:</strong> Upon signature
                    </p>
                    
                    <p>
                      This Confidentiality Agreement ("Agreement") is entered into between Solution Healthcare LLC community members to maintain a safe, private, and trusting environment for all participants.
                    </p>

                    <h5 className="font-bold text-gray-900 mt-4">1. What Stays in the Group</h5>
                    <p>
                      All personal stories, experiences, struggles, and information shared within our community—whether in group meetings, online forums, or private conversations—must remain confidential. What is shared in the group stays in the group.
                    </p>

                    <h5 className="font-bold text-gray-900 mt-4">2. Protected Information</h5>
                    <p>
                      This includes but is not limited to: personal experiences, mental health challenges, family situations, relationship issues, medical information, and any identifying details about other members. You may not share, repeat, or discuss another member's information outside the group.
                    </p>

                    <h5 className="font-bold text-gray-900 mt-4">3. Your Responsibility</h5>
                    <p>
                      By signing this agreement, you commit to respecting the privacy of all community members. You will not screenshot, record, photograph, or share any content from group discussions without explicit permission from all parties involved.
                    </p>

                    <h5 className="font-bold text-gray-900 mt-4">4. Safe Space Commitment</h5>
                    <p>
                      Our community thrives on trust. Members must feel safe to be vulnerable and authentic. Breaking confidentiality damages this trust and may result in removal from the community to protect other members.
                    </p>

                    <h5 className="font-bold text-gray-900 mt-4">5. Sharing Your Own Story</h5>
                    <p>
                      You are free to share your own experiences and what you've learned from the community. However, you must never include details that could identify other members or reveal their personal information.
                    </p>

                    <h5 className="font-bold text-gray-900 mt-4">6. Exceptions to Confidentiality</h5>
                    <p>
                      Confidentiality may be broken only when there is imminent danger to someone's safety, suspected abuse, or as required by law. In such cases, appropriate authorities or professionals will be contacted.
                    </p>

                    <h5 className="font-bold text-gray-900 mt-4">7. Consequences of Breach</h5>
                    <p>
                      Violating this confidentiality agreement will result in immediate removal from the community. We take privacy seriously and will take all necessary steps to protect our members.
                    </p>
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <label htmlFor="patient-name" className="block text-sm font-medium text-gray-700 mb-2">Full Legal Name</label>
                    <input 
                      type="text" 
                      id="patient-name"
                      name="patient_name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Enter your full legal name"
                    />
                  </div>

                  <div>
                    <label htmlFor="patient-email" className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <input 
                      type="email" 
                      id="patient-email"
                      name="patient_email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="patient-phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input 
                      type="tel" 
                      id="patient-phone"
                      name="patient_phone"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="(303) 725-6096"
                    />
                  </div>

                  <div>
                    <label htmlFor="date-of-birth" className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                    <input 
                      type="date" 
                      id="dateof-birth"
                      name="date_of_birth"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="signature" className="block text-sm font-medium text-gray-700 mb-2">Electronic Signature</label>
                    <input 
                      type="text" 
                      id="signature"
                      name="signature"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm font-serif"
                      placeholder="Type your full name as signature"
                    />
                    <p className="text-xs text-gray-500 mt-1">By typing your name, you agree to the terms above</p>
                  </div>

                  <div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        name="agreement_consent"
                        value="I agree to the confidentiality terms"
                        required
                        className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      />
                      <span className="text-sm text-gray-700">I have read and agree to the confidentiality agreement terms outlined above</span>
                    </label>
                  </div>
                </div>

                <button 
                  type="submit"
                  disabled={confidentialitySubmitting}
                  className="w-full px-6 py-4 bg-teal-600 text-white text-base font-semibold rounded-full hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {confidentialitySubmitting ? 'Submitting...' : 'Sign Agreement'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}